import type { Character } from '../domain/character.ts';
import type { DamageInput } from '../rules/damage.ts';
import { mitigateDamage } from '../rules/damage.ts';
import type { MitigationResult } from '../rules/damage.ts';
import { applyDamageToHealthBar, dyingRoundsFor, healSlots } from '../rules/health.ts';
import type { HealthBarDamageResult } from '../rules/health.ts';
import { spendMana, restoreMana } from '../rules/mana.ts';
import type { RestKind } from '../rules/rest.ts';
import { applyRest, REST_HOURS } from '../rules/rest.ts';
import { deriveStats } from './derivedStats.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';
import type { Effect } from '../rules/effects.ts';
import type { Item } from '../content/types.ts';
import { applyChanges } from '../rules/characterChanges.ts';
import { batch } from './characterModificationService.ts';
import type { CharacterChange } from '../domain/characterChange.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { advanceTime, SECONDS_PER_HOUR } from '../rules/gameTime.ts';
import { activeEffects, applyRestToConditions } from '../rules/encounter.ts';
import { buildPetEquipment } from './petEquipmentService.ts';
import { restorePetMana } from './petProgressionService.ts';

/**
 * Applies the results of the rules engine back onto a Character record.
 * Contains no rules of its own: it derives, calls the engine, and stores.
 */

export interface DamageOutcome {
  character: Character;
  mitigation: MitigationResult;
  healthBar: HealthBarDamageResult;
}

/** Heal Spell: every crawler starts with it. Cost 2 Mana, heals 2 slots (C&C p.15). */
// HEAL_SPELL_MANA_COST and HEAL_SPELL_SLOTS lived here and were hard-coded
// copies of C&C p.38 that no rule could keep honest. Heal is now cast through
// `castSupportSpell`, which reads the Skill's `manaCost` and the authored
// effect. There is exactly one mechanical Heal.

export function takeDamage(
  character: Character,
  input: DamageInput,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
): DamageOutcome {
  const derived = deriveStats(character, effects, lookupItem);
  const mitigation = mitigateDamage(input, derived.mitigation);
  const healthBar = applyDamageToHealthBar(derived.healthBar, mitigation.final);

  return {
    character: write(character, 'Took damage', [
      { kind: 'set-health-bar-slots', slotsRemaining: healthBar.bar.slotsRemaining },
      {
        kind: 'set-dying-rounds',
        dyingRoundsRemaining: dyingRoundsAfter(character, derived, mitigation, healthBar, {
          effects,
          lookupItem,
        }),
      },
    ]),
    mitigation,
    healthBar,
  };
}


export interface PostMitigationDamageOutcome {
  character: Character;
  healthBar: HealthBarDamageResult;
}

/**
 * Manual sheet damage after the GM has already resolved DR/resistance.
 *
 * The old Owlbear Sheet tab changed only `healthBarSlotsRemaining`, which meant
 * a crawler reduced to 0% through this control never received the Dying
 * countdown. This is the engine-owned equivalent: same post-mitigation input,
 * but the Health Bar and Dying state move together.
 */
export function takePostMitigationDamage(
  character: Character,
  damage: number,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
): PostMitigationDamageOutcome {
  const derived = deriveStats(character, effects, lookupItem);
  const healthBar = applyDamageToHealthBar(derived.healthBar, damage);
  const mitigation: MitigationResult = { final: damage, steps: [], effectsCancelled: false };
  return {
    character: write(character, 'Took post-mitigation damage', [
      { kind: 'set-health-bar-slots', slotsRemaining: healthBar.bar.slotsRemaining },
      {
        kind: 'set-dying-rounds',
        dyingRoundsRemaining: dyingRoundsAfter(character, derived, mitigation, healthBar, { effects, lookupItem }),
      },
    ]),
    healthBar,
  };
}

/**
 * RULE DCC-HP-004 - the Dying countdown after one instance of damage.
 *
 * TWO DISTINCT BRANCHES, and conflating them was the defect.
 *
 * NEWLY REDUCED TO ZERO. The crawler was alive when the hit landed, so this is
 * where the countdown STARTS: Con Mod rounds (Rules p.42). It is not also a
 * "damaged while Dying" hit - they were not Dying when it arrived - so nothing
 * is subtracted for the blow that put them there.
 *
 * ALREADY DYING. Rules p.42: "Each time a dying crawler would take damage" the
 * countdown drops by one. It used to drop by NOTHING, because
 * `applyDamageToHealthBar` cannot lose a slot from an empty bar, so
 * `reducedToZero` was false and the old code returned the countdown untouched.
 * A crawler at 0% could be hit forever and never get closer to dying.
 *
 * WHAT COUNTS AS "WOULD TAKE DAMAGE" is the source's ordinary threshold, not
 * `final > 0`. Rules p.42: damage smaller than one Health Bar slot "takes no
 * damage" - so a Dying crawler shrugging off 3 against a slot worth 4 has not
 * been damaged and the countdown holds. That keeps DR, Resistance and Immunity
 * worth having at 0%: mitigating a hit below one slot, or to nothing at all,
 * still protects the crawler.
 *
 * ONE INSTANCE, ONE DECREMENT. A hit worth five slots is one hit. The countdown
 * measures blows taken, not damage volume.
 */
function dyingRoundsAfter(
  character: Character,
  derived: { healthBar: { slotValue: number } },
  mitigation: MitigationResult,
  healthBar: HealthBarDamageResult,
  context: { effects: readonly Effect[]; lookupItem?: (itemId: string) => Item | undefined },
): number | null {
  if (healthBar.reducedToZero) {
    // Built the SAME way `derived` was. Deriving the Con Mod from a bare
    // character here meant armour reduced the damage while a Con-boosting item
    // did not extend the Dying rounds - one crawler, two different Con scores.
    return dyingRoundsFor(
      buildEffectiveCharacter(character, context.effects, context.lookupItem).statMod('con'),
    );
  }

  const countdown = character.dyingRoundsRemaining;
  const alreadyDying = character.healthBarSlotsRemaining === 0 && countdown !== null;
  if (!alreadyDying) return countdown;

  // The SAME effective slot value the mitigation was measured against. A second
  // value derived from the bare character would let a Con-boosting item change
  // the threshold in one place and not the other.
  const damaging = mitigation.final >= derived.healthBar.slotValue;
  if (!damaging) return character.dyingRoundsRemaining;

  return Math.max(0, countdown - 1);
}

/**
 * Play-time writes go through the shared modification layer, so damage, healing
 * and Mana are validated and audited exactly like a manual edit.
 */
function write(
  character: Character,
  reason: string,
  changes: readonly CharacterChange[],
): Character {
  const result = applyChanges(character, batch('play', reason, changes), {
    now: character.updatedAt,
  });
  return result.ok ? result.character : character;
}

export function healCharacterSlots(
  character: Character,
  slots: number,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
): Character {
  const derived = deriveStats(character, effects, lookupItem);
  const bar = healSlots(derived.healthBar, slots);
  return write(character, 'Healed', [
    { kind: 'set-health-bar-slots', slotsRemaining: bar.slotsRemaining },
    // Rules p.42: a Dying crawler must be healed to at least 10% to survive.
    {
      kind: 'set-dying-rounds',
      dyingRoundsRemaining: bar.slotsRemaining > 0 ? null : character.dyingRoundsRemaining,
    },
  ]);
}

export type SpendManaOutcome =
  | { ok: true; character: Character }
  | { ok: false; reason: string };

export function spendCharacterMana(
  character: Character,
  cost: number,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
): SpendManaOutcome {
  const derived = deriveStats(character, effects, lookupItem);
  const result = spendMana(derived.mana, cost);
  if (!result.ok) return result;
  return {
    ok: true,
    character: write(character, 'Spent Mana', [
      { kind: 'set-current-mana', currentMana: result.pool.current },
    ]),
  };
}

export function restoreCharacterMana(
  character: Character,
  amount: number,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
): Character {
  const derived = deriveStats(character, effects, lookupItem);
  return write(character, 'Restored Mana', [
    { kind: 'set-current-mana', currentMana: restoreMana(derived.mana, amount).current },
  ]);
}

export interface RestOutcome {
  character: Character;
  removesFatigued: boolean;
  recoversLongTermInjuries: boolean;
  /** The runtime state after conditions ending at this rest were cleared. */
  runtime: RuntimeState | null;
  /** Conditions that ended because of the rest, e.g. Fatigued at a long rest. */
  expiredEffectIds: string[];
  /** Cooldowns that finished during the hours the rest took. */
  endedCooldownIds: string[];
  /** In-game seconds the rest consumed. */
  elapsedSeconds: number;
}

export function restCharacter(
  character: Character,
  kind: RestKind,
  runtime: RuntimeState | null = null,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
  now: string = character.updatedAt,
): RestOutcome {
  const derived = deriveStats(character, effects, lookupItem);
  const result = applyRest({ bar: derived.healthBar, mana: derived.mana }, kind);

  // RULE DCC-COND-010 - conditions that end with a rest end here, through the
  // ordinary rest flow. There is no condition-specific rest logic.
  const restKind = REST_TO_CONDITION_REST[kind];
  const cleared = runtime && restKind ? applyRestToConditions(runtime, restKind) : null;
  const afterRestConditions = cleared ? cleared.runtime : runtime;

  // RULE DCC-TIME-001 - a rest TAKES the hours it is printed as taking
  // (Rules p.43: Short 2, Long 8, Full Day's 30, and an hour of mending). Those
  // hours pass for everything else too, so a ten-minute Debuff cannot survive a
  // long rest and a 30-hour cooldown is genuinely closer to done.
  //
  // Applied AFTER the explicit end-of-rest conditions, which is only a matter
  // of reporting: an Effect ending both ways is removed either way, and doing
  // the rest first keeps it in `expiredEffectIds` as ended BY the rest, which
  // is what the player asked for.
  const seconds = REST_HOURS[kind] * SECONDS_PER_HOUR;
  const passed = afterRestConditions
    ? advanceTime(afterRestConditions, seconds)
    : { runtime: null, expiredEffectIds: [] as string[], endedCooldownIds: [] as string[] };

  // A rest may itself remove a Buff/Debuff that was raising Max Mana. Recovery
  // is calculated with the effects that were active when the rest began, but
  // the stored Current Mana must be legal AFTER those expirations. This mirrors
  // round cleanup: never leave Current Mana above the post-cleanup maximum.
  const postRestEffects = passed.runtime ? activeEffects(passed.runtime) : effects;
  const postRestMaxMana = deriveStats(character, postRestEffects, lookupItem).mana.max;
  const currentMana = Math.min(result.mana.current, postRestMaxMana);

  // PROJECT MANA OVERRIDE DCC-MANA-005 applies to companions as well. Pets
  // spend from their own Mana pool, but a safe hour/rest restores that pool to
  // its own Max Mana just as it does for the owning crawler.
  const restedCompanions = character.companions.map((pet) => restorePetMana(pet, now, lookupItem ? buildPetEquipment(character, pet, lookupItem) : undefined));
  const characterWithRestedPets = restedCompanions.some((pet, index) => pet !== character.companions[index])
    ? { ...character, companions: restedCompanions }
    : character;

  return {
    character: write(characterWithRestedPets, `Rested (${kind})`, [
      { kind: 'set-health-bar-slots', slotsRemaining: result.bar.slotsRemaining },
      { kind: 'set-current-mana', currentMana },
      {
        kind: 'set-dying-rounds',
        dyingRoundsRemaining: result.bar.slotsRemaining > 0 ? null : character.dyingRoundsRemaining,
      },
    ]),
    removesFatigued: result.removesFatigued,
    recoversLongTermInjuries: result.recoversLongTermInjuries,
    runtime: passed.runtime,
    expiredEffectIds: [...(cleared ? cleared.expiredEffectIds : []), ...passed.expiredEffectIds],
    endedCooldownIds: passed.endedCooldownIds,
    elapsedSeconds: seconds,
  };
}

/**
 * Which rest tier a rest counts as for condition durations. An hour of mending
 * is not a rest tier, so it clears nothing.
 */
const REST_TO_CONDITION_REST: Record<RestKind, 'short' | 'long' | 'full-day' | null> = {
  'mend-hour': null,
  short: 'short',
  long: 'long',
  'full-day': 'full-day',
};
